import {useAlive} from '@github-ui/use-alive'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import type {IssueComment} from '../page-data/payloads/issue-comments'
import type {AliveChannels} from '../page-data/payloads/header'
import {useIssueComments, useIssueCommentsActions, usePullRequestBasePath} from '../stores/selectors'
import {useCallback} from 'react'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {reactFetch} from '@github-ui/verified-fetch'

interface IssueCommentAliveData {
  timestamp: number
  wait?: number
  reason: string
  gid: string
}

const ISSUE_COMMENT_REASON_PATTERN = /^issue comment #(\d+) updated$/

/**
 * Subscribes to alive events on the pull request timeline channel and updates
 * issue comments when a comment-related event is received.
 *
 * The caller provides the alive channels object. The hook only activates the
 * subscription once comments have been loaded into the store (i.e. after the
 * comments panel has been opened at least once).
 *
 * When another user modifies a PR-level comment, this hook fetches the updated
 * comment from the server and replaces it in the store.
 */
export function useIssueCommentLiveUpdates(aliveChannels: AliveChannels | undefined): void {
  const basePath = usePullRequestBasePath()
  const {updateIssueComment} = useIssueCommentsActions()
  const issueComments = useIssueComments()

  const liveUpdatesEnabled = isFeatureEnabled('prs_live_updates_issue_comments')

  // Only subscribe when comments have been loaded into the store.
  // This avoids subscribing on every PR page load — the subscription
  // activates lazily once the panel is opened and comments are fetched.
  const aliveChannel = liveUpdatesEnabled && issueComments.length > 0 ? aliveChannels?.pullRequestTimeline : undefined

  const handleAliveNotification = useCallback(
    (data: IssueCommentAliveData) => {
      const match = ISSUE_COMMENT_REASON_PATTERN.exec(data.reason)
      if (!match?.[1]) return

      const commentId = match[1]

      const fetchComment = async () => {
        try {
          const url = `${basePath}/page_data/${PageData.issueComments}/${encodeURIComponent(commentId)}`
          const response = await reactFetch(url)
          if (!response.ok) return

          const updatedComment = (await response.json()) as IssueComment
          updateIssueComment({
            match: comment => comment.id === commentId,
            transform: () => updatedComment,
          })
        } catch {
          // Silently fail — live updates are non-critical
        }
      }

      if (data.wait) {
        window.setTimeout(() => void fetchComment(), data.wait)
      } else {
        void fetchComment()
      }
    },
    [basePath, updateIssueComment],
  )

  useAlive(aliveChannel, handleAliveNotification)
}
